import {
  Box,
  Table,
  TableBody,
  TablePagination,
  Typography,
  Skeleton,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  IconButton,
  Tooltip,
  Switch,
} from '@material-ui/core';
import { FC, useEffect, useRef, useState } from 'react';
import Label from 'src/components/shared/Label';
import { dateFormat } from 'src/constants';
import { IColumn } from 'src/components/dataTable/tableModel';
import PencilAltIcon from '../../icons/PencilAlt';
import { CancelPresentation, Delete, DoDisturbAlt, Refresh } from '@material-ui/icons';

interface IConfigUserTable {
  columns: IColumn[];
  dataList: any[];
  isLoading: boolean;
  isSearchable?: boolean;
  onClickEdit?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleResendEmail?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClickDelete?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleInvalidateInvite?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleEnabledChange?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  columns: IColumn[];
  onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
  onClickEdit?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  order: Order;
  orderBy: string;
}

const getTFLabel = (bool: Boolean): JSX.Element => {
  let text = '';
  let color: any = 'success';
  if (bool) {
    text = 'Yes';
    color = 'success';
  } else {
    text = 'No';
    color = 'warning';
  }
  return (
    <Label color={color}>
      {text}
    </Label>
  );
};
function descendingComparator<T>(a: T, b: T, orderBy: any) {
  const frst: any = typeof a[orderBy] === 'string' ? a[orderBy].toLowerCase() : a[orderBy];
  const scnd: any = typeof b[orderBy] === 'string' ? b[orderBy].toLowerCase() : b[orderBy];
  if (scnd < frst) {
    return -1;
  }
  if (scnd > frst) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, columns, onClickEdit, onRequestSort } = props;
  const createSortHandler = (property: any) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow style={{ position: "sticky", top: 0, zIndex: 1 }}>
        {columns.map((headCell, i) => (
          <TableCell
            width={headCell.width}
            key={i.toString()}
            align={headCell.align ?? 'left'}
            sortDirection={orderBy === headCell.headerName ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.field}
              direction={orderBy === headCell.field ? order : 'asc'}
              onClick={createSortHandler(headCell.field)}
            >
              {headCell.headerName}
            </TableSortLabel>
          </TableCell>
        ))}
        {
          (onClickEdit) && (
            <TableCell width="125px" align='center'>
              Actions
            </TableCell>
          )
        }
      </TableRow>
    </TableHead>
  );
}

function Row(props) {
  const { row, columns, onClickEdit, onClickDelete, handleResendEmail, handleInvalidateInvite, handleEnabledChange } = props;

  const shouldAllowResend = row.Status === 'Invite Expired';

  const shouldAllowDelete = onClickDelete && row.Status !== 'User registered';

  const shouldAllowEditOrInvalidate = handleInvalidateInvite && row.Status !== 'Invite invalidated' && row.Status !== 'User registered' && row.status !== 'Invite Expired';

  const getSwitchForBoolean = (column) => {
    return (
      <Switch
        checked={row[column.field]}
        onChange={() => handleEnabledChange(row)}
        color="primary"
      />
    )
  };

  return (
    <>
      <TableRow hover role="checkbox" tabIndex={-1}>
        {columns.map((column, idx) => {
          const value = row[column.field];
          let cellAlign = column.align;

          // Adjust alignment based on value
          if (value === null || value === '-') {
            cellAlign = 'left';
          }

          return (
            <TableCell
              align={cellAlign}
              key={idx.toString()}
              style={{
                maxWidth: '200px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {column?.dataType === 'boolean' ? (
                <span>
                  {(() => {
                    if (value !== null && value !== '-') {
                      if (column.allowInlineChangeForBoolean) {
                        return getSwitchForBoolean(column);
                      }
                      return getTFLabel(value);
                    }
                    return value;
                  })()}
                </span>
              ) : (
                <Tooltip title={value || ''} arrow>
                  <span>
                    {(() => {
                      if (column?.dataType === 'Date' && value) {
                        return dateFormat(value);
                      }
                      if (column?.dataType === 'boolean' && value !== null && value !== '-') {
                        if (column.allowInlineChangeForBoolean) {
                          return getSwitchForBoolean(column);
                        }
                        return getTFLabel(value);
                      }
                      return value;
                    })()}
                  </span>
                </Tooltip>
              )}
            </TableCell>
          );
        })}
        {
          <TableCell>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}>
              {shouldAllowResend ? (
                <Tooltip title='Resend Email'>
                  <IconButton sx={{ p: '5px' }} onClick={() => handleResendEmail(row)}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <IconButton sx={{ p: '5px', visibility: 'hidden' }}>
                  <Refresh fontSize="small" />
                </IconButton>
              )}
              {shouldAllowEditOrInvalidate ? (
                <Tooltip title='Invalidate'>
                  <IconButton sx={{ p: '5px' }} onClick={() => handleInvalidateInvite(row)}>
                    <DoDisturbAlt fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <IconButton sx={{ p: '5px', visibility: 'hidden' }}>
                  <CancelPresentation fontSize="small" />
                </IconButton>
              )}
              {shouldAllowEditOrInvalidate ? (
                <Tooltip title='Edit'>
                  <IconButton sx={{ p: '5px' }} onClick={() => onClickEdit(row)}>
                    <PencilAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <IconButton sx={{ p: '5px', visibility: 'hidden' }}>
                  <PencilAltIcon fontSize="small" />
                </IconButton>
              )}

              {shouldAllowDelete ? (
                <Tooltip title='Delete'>
                  <IconButton sx={{ p: '5px' }} onClick={() => onClickDelete(row)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <IconButton sx={{ p: '5px', visibility: 'hidden' }}>
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </div>
          </TableCell>
        }
      </TableRow>
    </>
  );
}

const ConfigUserTable: FC<IConfigUserTable> = (props) => {
  const { columns = [], isLoading, dataList, onClickEdit, onClickDelete, handleResendEmail, handleInvalidateInvite, handleEnabledChange } = props;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('');
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const tableRef = useRef<HTMLTableElement>(null);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    setTableData(dataList);
  }, [dataList]);

  return (
    <>
      <TableContainer component={Paper} style={{ maxHeight: "70vh", minHeight: "30vh" }}>
        {isLoading ? (
          <>
            <Skeleton sx={{ ml: 2, mr: 2, height: 70 }} />
            {Array.from(Array(5).keys()).map((index) => <Skeleton key={index} sx={{ ml: 2, mr: 2, mt: -2, height: 70 }} />)}
          </>
        ) : (
          <>
            <Table ref={tableRef} stickyHeader>
              <EnhancedTableHead
                columns={columns}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                onClickEdit={onClickEdit}
              />
              <TableBody>
                {stableSort(tableData, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, idx) => (
                    <Row
                      key={idx.toString()}
                      row={row}
                      columns={columns}
                      onClickEdit={onClickEdit}
                      onClickDelete={onClickDelete}
                      handleResendEmail={handleResendEmail}
                      handleInvalidateInvite={handleInvalidateInvite}
                      handleEnabledChange={handleEnabledChange}
                    />
                  ))
                }
              </TableBody>
            </Table>
          </>
        )}
        {!isLoading && tableData.length === 0 && (
          <>
            <Box sx={{ height: 200, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" sx={{ pl: 0 }}>
                No data found!
              </Typography>
            </Box>
          </>
        )}
      </TableContainer>
      {tableData.length > 0 && (
        <TablePagination
          style={{ marginRight: 40 }}
          component="div"
          count={tableData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}
    </>
  );
};


export default ConfigUserTable;